/**
 * TikTok Integration
 *
 * NOTE: TikTok does not provide a public API for content fetching.
 * This service manages manually configured TikTok embeds.
 *
 * For production, consider:
 * 1. Manual embed URL configuration
 * 2. TikTok for Developers API (requires approval)
 * 3. Third-party services (with proper authorization)
 */

import config from '../config';

class TikTokService {
  constructor() {
    this.cache = new Map();
    this.manualEmbeds = [];
  }

  /**
   * Load manually configured TikTok videos
   * In production, these would come from a CMS or config file
   */
  async getVideos() {
    const cacheKey = 'tiktok_videos';

    // Check cache
    if (this.isCacheValid(cacheKey)) {
      return this.cache.get(cacheKey).data;
    }

    // Return manually configured embeds
    // In production, fetch from CMS or configuration
    const videos = this.getManualEmbeds();

    this.setCacheData(cacheKey, videos);
    return videos;
  }

  /**
   * Get manually configured embeds
   * This should be replaced with CMS integration or admin panel
   */
  getManualEmbeds() {
    // Example structure - replace with actual embed URLs
    return this.manualEmbeds.map(embed => ({
      id: embed.id,
      platform: 'tiktok',
      embedUrl: embed.url,
      caption: embed.caption || '',
      hashtags: this.extractHashtags(embed.caption || ''),
      timestamp: embed.timestamp || new Date().toISOString(),
      section: embed.section || 'default', // Manual section assignment
    }));
  }

  /**
   * Add a manual embed (admin function)
   */
  addManualEmbed(embedData) {
    const embed = {
      id: `tiktok_${Date.now()}`,
      url: embedData.url,
      caption: embedData.caption || '',
      section: embedData.section || 'default',
      timestamp: new Date().toISOString(),
    };

    this.manualEmbeds.push(embed);
    this.cache.clear(); // Clear cache to force refresh

    return embed;
  }

  /**
   * Extract TikTok video ID from URL
   */
  extractVideoId(url) {
    const match = url.match(/\/video\/(\d+)/);
    return match ? match[1] : null;
  }

  /**
   * Generate TikTok embed URL
   */
  generateEmbedUrl(videoId) {
    return `https://www.tiktok.com/embed/v2/${videoId}`;
  }

  /**
   * Extract hashtags from caption
   */
  extractHashtags(caption) {
    const hashtagRegex = /#[\w]+/g;
    const matches = caption.match(hashtagRegex);
    return matches ? matches.map(tag => tag.toLowerCase()) : [];
  }

  /**
   * Filter videos by section
   */
  filterBySection(videos, section) {
    return videos.filter(video => video.section === section);
  }

  /**
   * Load embeds from configuration
   * This would typically load from a JSON file or CMS
   */
  loadFromConfig(embedsConfig) {
    this.manualEmbeds = embedsConfig;
    this.cache.clear();
  }

  /**
   * Cache management
   */
  isCacheValid(key) {
    const cached = this.cache.get(key);
    if (!cached) return false;
    return Date.now() - cached.timestamp < config.cache.tiktok;
  }

  setCacheData(key, data) {
    this.cache.set(key, {
      data,
      timestamp: Date.now(),
    });
  }

  /**
   * Clear cache
   */
  clearCache() {
    this.cache.clear();
  }
}

export default new TikTokService();
